import type { RouteNamedMap, _RouterTyped } from 'unplugin-vue-router'
import { canNavigate } from '@layouts/plugins/casl'
import { getActiveAccount } from '@/plugins/auth/msalConfig'
import { ability } from '@/plugins/casl/ability'
import { PermissionsLoadedKey, loadAbilityRules } from '@/plugins/casl/permissions'

// DAY-37: refresh the user's permissions once per browser session, so a role or matrix change
// (or a session that logged in before DAY-37 with "manage all") shows up without logging out.
// If the load fails the user sees no module for now and it is tried again on the next navigation.
const refreshPermissionsOnce = async () => {
  if (sessionStorage.getItem(PermissionsLoadedKey))
    return

  const rules = await loadAbilityRules()

  useCookie('userAbilityRules').value = JSON.stringify(rules ?? [])
  ability.update(rules ?? [])
  if (rules)
    sessionStorage.setItem(PermissionsLoadedKey, '1')
}

export const setupGuards = (router: _RouterTyped<RouteNamedMap & { [key: string]: any }>) => {
  // 👉 router.beforeEach
  // Docs: https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards
  router.beforeEach(async (to) => {
    /*
     * If it's a public route, continue navigation. This kind of pages are allowed to visited by login & non-login users. Basically, without any restrictions.
     * Examples of public routes are, 404, under maintenance, etc.
     */
    if (to.meta.public) return

    /**
     * Check if user is logged in by checking if token & user data exists in local storage
     * Feel free to update this logic to suit your needs
     */

    const isLoggedIn = !!(useCookie('userData').value && useCookie('accessToken').value)

    /*
      If user is logged in and is trying to access login like page, redirect to home
      else allow visiting the page
      (WARN: Don't allow executing further by return statement because next code will check for permissions)
     */
    if (to.meta.unauthenticatedOnly) {
      if (isLoggedIn) return '/'
      else return undefined
    }

    // DAY-37: pages without a permission are open to signed-in users only, so check the
    // session first (before, the "manage all" rule did this implicitly).
    if (!isLoggedIn && to.matched.length) {
      return {
        name: 'login',
        query: {
          ...to.query,
          to: to.fullPath !== '/' ? to.path : undefined,
        },
      }
    }

    if (isLoggedIn)
      await refreshPermissionsOnce()

    if (!canNavigate(to, ability) && to.matched.length)
      return { name: 'not-authorized' }
  })
}
